import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Button } from '../base/Button';
import './HighlightedPDFViewer.css';
import { Tooltip } from 'antd';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const Highlight = ({ highlight, scale, randomColors }) => {
    return (React.createElement("div", null,
        React.createElement("div", { style: {
                position: 'absolute',
                left: highlight.x * scale,
                top: highlight.y * scale,
                width: highlight.width * scale,
                height: highlight.height * scale,
                backgroundColor: randomColors ? highlight.color : 'rgba(255, 255, 0, 0.5)',
            }, onKeyDown: () => { }, role: "button", tabIndex: 0 })));
};
export function HighlightedPDFViewer({ maxHeight, fileUrl, document, highlights, randomColors = false, onClose, }) {
    const viewerRef = useRef(null);
    const [{ numPages }, setState] = useState({
        numPages: null,
    });
    const [pageNumber, setPageNumber] = useState(1);
    const [pageWidth, setPageWidth] = useState(2048);
    const [scale, setScale] = useState(1); // Zoom scale
    const [pdfHighlights, setPdfHighlights] = useState([]);
    useEffect(() => {
        if (highlights && highlights.length > 0) {
            setPdfHighlights(highlights);
            // we can default from the first highlight
            setPageNumber(highlights[0].page);
            setPageWidth((prev) => highlights[0].pageWidth || prev);
        }
    }, [highlights]);
    // Functions to handle page navigation
    const goToNextPage = () => {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };
    const goToPreviousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };
    function onDocumentLoadSuccess({ numPages }) {
        setState({
            numPages,
        });
    }
    const updateScale = () => {
        if (viewerRef.current) {
            const viewerWidth = viewerRef.current.clientWidth;
            const scaleFactor = viewerWidth / pageWidth;
            setScale(scaleFactor);
        }
    };
    const zoomIn = () => {
        setScale((prevScale) => prevScale + 0.1); // Cap the zoom level at 3x
    };
    const zoomOut = () => {
        setScale((prevScale) => prevScale - 0.1); // Minimum zoom level at 0.5x
    };
    // Adjust the scale based on the width of the viewer (initial zoom)
    useEffect(() => {
        updateScale();
        // Update the scale when the window is resized
        window.addEventListener('resize', updateScale);
        // Cleanup the event listener on component unmount
        return () => window.removeEventListener('resize', updateScale);
    }, [pageWidth]);
    return (React.createElement("div", { ref: viewerRef, style: { paddingTop: 10 } },
        React.createElement("div", { className: "blue", style: { fontWeight: 500 } },
            "File: ",
            document.filename),
        React.createElement("div", { style: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                position: 'relative',
                paddingTop: 20,
                paddingBottom: 20,
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    gap: 10,
                } },
                React.createElement(Button, { onClick: goToPreviousPage, disabled: pageNumber <= 1, style: { border: 0, backgroundColor: 'transparent' }, text: "<" }),
                React.createElement("span", null,
                    "Page ",
                    pageNumber,
                    " of ",
                    numPages),
                React.createElement(Button, { onClick: goToNextPage, disabled: pageNumber >= numPages, style: { border: 0, backgroundColor: 'transparent' }, text: ">" })),
            React.createElement("div", { style: { display: 'flex', marginLeft: 'auto', gap: 10 } },
                React.createElement(ZoomOutOutlined, { onClick: zoomOut, disabled: scale <= 0.5 }),
                React.createElement(ZoomInOutlined, { onClick: zoomIn, disabled: scale >= 3 }),
                React.createElement(Tooltip, null,
                    React.createElement("div", { className: "clickable", style: { color: 'red', fontWeight: 700 }, onClick: onClose, onKeyDown: () => { }, role: "button", tabIndex: 0 }, "X")))),
        React.createElement("div", { style: {
                display: 'flex',
                maxHeight: maxHeight || 700,
                overflow: 'auto',
                justifyContent: 'center',
            } },
            React.createElement(Document, { file: document?.url || fileUrl, onLoadSuccess: onDocumentLoadSuccess },
                React.createElement("div", { key: `page_${pageNumber}`, style: { position: 'relative', marginBottom: '20px' } },
                    React.createElement(Page, { id: `${pageNumber}`, pageNumber: pageNumber, loading: '', scale: scale, width: pageWidth }),
                    pdfHighlights
                        .filter((highlight) => highlight.page === pageNumber)
                        .map((highlight, idx) => (React.createElement("div", { key: idx },
                        React.createElement(Highlight, { highlight: highlight, scale: scale, randomColors: randomColors })))))))));
}
